/**
 * GIS 管理 (docs/22、Sprint G2) の Firestore / Storage / callable wrapper。
 *
 *   - useGisConfig / setGisEnabled        : gisConfig/main (GIS ON/OFF、要件1)
 *   - useGisLayers                        : gisLayers 購読 (displayOrder 昇順、要件2)
 *   - updateGisLayer / moveGisLayer       : スタイル / 表示順 / 表示既定 編集 (要件3)
 *   - importGisFile                       : 生ファイル upload → importGisLayer callable (要件2)
 *   - deleteGisLayer                      : deleteGisLayer callable
 *   - fetchGisGeoJson                     : 変換済み GeoJSON を Storage から取得 (プレビュー用)
 *
 * 認可は firestore.rules / storage.rules で master + 担当 district admin に限定済。
 */

import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getBytes, ref as storageRef, uploadBytes } from 'firebase/storage';
import { useEffect, useState } from 'react';
import type { GisLayer, GisLayerStyle } from '@citizen-walk/shared-types';
import { db, functions, storage } from '../../lib/firebase';

const VALID_EXT = /\.(gpkg|kml|kmz|geojson|json)$/i;

// ── gisConfig (ON/OFF) ───────────────────────────────────────────────────────
export type GisConfigState =
  | { status: 'loading'; enabled: boolean; error: null }
  | { status: 'ready'; enabled: boolean; error: null }
  | { status: 'error'; enabled: boolean; error: Error };

export function useGisConfig(districtId: string | null): GisConfigState {
  const [state, setState] = useState<GisConfigState>({
    status: 'loading',
    enabled: false,
    error: null,
  });
  useEffect(() => {
    if (!districtId) return;
    const ref = doc(db, 'districts', districtId, 'gisConfig', 'main');
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const enabled = snap.exists() ? Boolean(snap.data()?.enabled) : false;
        setState({ status: 'ready', enabled, error: null });
      },
      (error) => setState({ status: 'error', enabled: false, error }),
    );
    return () => unsub();
  }, [districtId]);
  return state;
}

/** GIS ON/OFF を書込む。初回は DocCommon を補完して create、以降は enabled のみ merge 更新。 */
export async function setGisEnabled(
  districtId: string,
  enabled: boolean,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'gisConfig', 'main');
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await setDoc(ref, { enabled, updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true });
  } else {
    await setDoc(ref, {
      enabled,
      schemaVersion: 1,
      createdAt: serverTimestamp(),
      createdBy: uid,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
      archivedAt: null,
    });
  }
}

// ── gisLayers (一覧) ─────────────────────────────────────────────────────────
export type GisLayersState =
  | { status: 'loading'; layers: GisLayer[]; error: null }
  | { status: 'ready'; layers: GisLayer[]; error: null }
  | { status: 'error'; layers: GisLayer[]; error: Error };

function mapLayerDoc(snap: { id: string; data: () => DocumentData }): GisLayer {
  return { ...(snap.data() as Omit<GisLayer, 'id'>), id: snap.id };
}

export function useGisLayers(districtId: string | null): GisLayersState {
  const [state, setState] = useState<GisLayersState>({
    status: 'loading',
    layers: [],
    error: null,
  });
  useEffect(() => {
    if (!districtId) return;
    const q = query(collection(db, 'districts', districtId, 'gisLayers'), orderBy('displayOrder'));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => setState({ status: 'ready', layers: snap.docs.map(mapLayerDoc), error: null }),
      (error) => setState({ status: 'error', layers: [], error }),
    );
    return () => unsub();
  }, [districtId]);
  return state;
}

/** Firestore は undefined を拒否するため、style から undefined キーを除去する。 */
function pruneStyle(style: GisLayerStyle): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(style)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export interface GisLayerPatch {
  name?: string;
  style?: GisLayerStyle;
  displayOrder?: number;
  defaultVisible?: boolean;
}

/** レイヤーの name / style / displayOrder / defaultVisible を更新 (要件3)。 */
export async function updateGisLayer(
  districtId: string,
  layerId: string,
  patch: GisLayerPatch,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'gisLayers', layerId);
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  };
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.displayOrder !== undefined) payload.displayOrder = patch.displayOrder;
  if (patch.defaultVisible !== undefined) payload.defaultVisible = patch.defaultVisible;
  // style は updateDoc で **丸ごと置換** (categorical を OFF にしたら確実に消える。setDoc merge だと
  // nested map が深くマージされ古い categorical が残るため、updateDoc を使う)。
  if (patch.style !== undefined) payload.style = pruneStyle(patch.style);
  await updateDoc(ref, payload);
}

/**
 * レイヤーを 1 つ上 (dir=-1) / 下 (dir=+1) に移動し、displayOrder を contiguous に正規化する (要件3)。
 * displayOrder 重複時も index ベースで再採番するため安定。
 */
export async function moveGisLayer(
  districtId: string,
  layers: GisLayer[],
  layerId: string,
  dir: -1 | 1,
  uid: string,
): Promise<void> {
  const sorted = [...layers].sort(
    (a, b) => a.displayOrder - b.displayOrder || a.id.localeCompare(b.id),
  );
  const idx = sorted.findIndex((l) => l.id === layerId);
  const swap = idx + dir;
  if (idx < 0 || swap < 0 || swap >= sorted.length) return;
  const tmp = sorted[idx]!;
  sorted[idx] = sorted[swap]!;
  sorted[swap] = tmp;
  await Promise.all(
    sorted.map((l, i) =>
      l.displayOrder === i
        ? Promise.resolve()
        : updateGisLayer(districtId, l.id, { displayOrder: i }, uid),
    ),
  );
}

// ── import / delete callable ─────────────────────────────────────────────────
export interface ImportedGisLayerSummary {
  id: string;
  name: string;
  geometryType: string;
  featureCount: number;
  sizeBytes: number;
  warning?: string;
}
export interface ImportGisLayerResult {
  ok: true;
  layers: ImportedGisLayerSummary[];
}

const importCallable = httpsCallable<
  { districtId: string; uploadId: string; fileName: string },
  ImportGisLayerResult
>(functions, 'importGisLayer');
const deleteCallable = httpsCallable<{ districtId: string; layerId: string }, { ok: true }>(
  functions,
  'deleteGisLayer',
);

/** 生ファイルを gis-uploads/ に upload → importGisLayer callable を実行 (要件2)。 */
export async function importGisFile(districtId: string, file: File): Promise<ImportGisLayerResult> {
  const ext = file.name.toLowerCase().match(VALID_EXT)?.[0];
  if (!ext) {
    throw new Error('対応形式は .gpkg / .kml / .kmz / .geojson です');
  }
  const uploadId = crypto.randomUUID();
  const path = `districts/${districtId}/gis-uploads/${uploadId}${ext}`;
  await uploadBytes(storageRef(storage, path), file);
  const res = await importCallable({ districtId, uploadId, fileName: file.name });
  return res.data;
}

export async function deleteGisLayer(districtId: string, layerId: string): Promise<void> {
  await deleteCallable({ districtId, layerId });
}

/** 変換済み GeoJSON を Storage から取得 (admin プレビュー用)。 */
export async function fetchGisGeoJson(storagePath: string): Promise<GeoJSON.FeatureCollection> {
  const bytes = await getBytes(storageRef(storage, storagePath));
  return JSON.parse(new TextDecoder().decode(bytes)) as GeoJSON.FeatureCollection;
}
